import { types, getParent, type Instance, type SnapshotIn } from 'mobx-state-tree';
import AttributeOwner from './attribute_owner';
import FSM from './fsm';
import MultiplePropagationConstraint from './multiple_propagation_constraint';
import type { IMetaModel } from './index';
import type { IDependency } from './dependency';
import type { IInheritance } from './inheritance';
import type { IMethod } from './method';
import type { IFSM } from './fsm';
import type { IMultiplePropagationConstraint } from './multiple_propagation_constraint';
import type { IAttribute } from './attribute';

// An edge in a MERODE path is a dependency or an inheritance.
type PathEdge = IDependency | IInheritance;

/**
 * The ObjectType instance interface.
 *
 * Like `IMetaModel`, this is HAND-DECLARED (not `Instance<typeof ObjectType>`)
 * to anchor the recursion. ObjectType embeds FSM and MultiplePropagationConstraint
 * arrays, and those children's `get objectType()` is annotated with `IObjectType`;
 * deriving this from the model would make FSM/MPC/Transition and ObjectType
 * mutually reference each other's *model* types and loop. Members mirror the
 * views/actions defined on the composed model below.
 */
export interface IObjectType {
  readonly abstract: boolean;
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly constraints: string;
  readonly fsms: IFSM[];
  readonly multiplePropagationConstraints: IMultiplePropagationConstraint[];
  readonly attributes: IAttribute[];
  readonly displayString: string;
  asString(): string;
  readonly metamodel: IMetaModel;
  readonly dependenciesAsDependent: IDependency[];
  readonly dependenciesAsMaster: IDependency[];
  readonly dependencies: IDependency[];
  readonly masters: IObjectType[];
  readonly inheritancesAsSubtype: IInheritance[];
  readonly inheritancesAsSupertype: IInheritance[];
  readonly inheritances: IInheritance[];
  readonly supertypes: IObjectType[];
  safeIndirectSupertypes(seen?: IObjectType[]): IObjectType[];
  safeIndirectSubtypes(seen?: IObjectType[]): IObjectType[];
  readonly indirectSupertypes: IObjectType[];
  readonly indirectSubtypes: IObjectType[];
  readonly subtypes: IObjectType[];
  readonly methods: IMethod[];
  readonly acquiredMethods: IMethod[];
  readonly ownedMethods: IMethod[];
  readonly inheritedMethods: IMethod[];
  readonly specialisedMethods: IMethod[];
  methodsForEventType(eventId: string): IMethod[];
  inheritedMethodsForEventType(eventId: string): IMethod[];
  readonly tdl: number;
  readonly bul: number;
  isExistenceDependentOf(other: IObjectType, start?: IObjectType | null): boolean;
  readonly defaultFSM: IFSM | undefined;
  readonly fsmForCodeGeneration: IFSM | undefined;
  getFSMById(id: string): IFSM | undefined;
  getMultiplePropagationConstraintById(id: string): IMultiplePropagationConstraint | undefined;
  pathsTo(target: IObjectType): PathEdge[][];
  inheritancePathTo(supertype: IObjectType): IInheritance[];
  getAttributeById(id: string): IAttribute | undefined;
  setName(name: string): void;
  setDescription(description: string): void;
  setAbstract(abstract: boolean): void;
  setConstraints(constraints: string): void;
  addFSM(
    id: string,
    name: string,
    type: 'DEFAULT' | 'USER_DEFINED' | 'CALCULATED',
    codegeneration: boolean
  ): IFSM;
  removeFSM(id: string): void;
  addMultiplePropagationConstraint(
    id: string,
    type: 'EQUAL_TO' | 'NOT_EQUAL_TO',
    paths: unknown
  ): IMultiplePropagationConstraint;
  removeMultiplePropagationConstraint(id: string): void;
  removeAttribute(id: string): void;
  addAttribute(
    id: string,
    name: string,
    type: string,
    description: string,
    implementation: string,
    validation: string
  ): IAttribute;
}

function generatePathsBetween(
  source: IObjectType,
  target: IObjectType,
  edges: PathEdge[]
): PathEdge[][] {
  const paths: PathEdge[][] = [];
  if (source === target) {
    if (edges.length > 0) {
      paths.push(edges);
    }
  } else {
    // Dependency based paths
    for (const dep of source.dependenciesAsMaster) {
      const currentEdges = edges.slice();
      currentEdges.push(dep);
      paths.push(...generatePathsBetween(dep.dependent, target, currentEdges));
    }
    // Inheritance based paths
    if (edges.length > 0) {
      // Only if there is already a dependency in the path (MPC paths shouldn't start with an inheritance)
      for (const inh of source.inheritancesAsSupertype) {
        const currentEdges = edges.slice();
        currentEdges.push(inh);
        paths.push(...generatePathsBetween(inh.subtype, target, currentEdges));
      }
    }
  }
  return paths;
}

/**
 * A MERODE object type.
 *
 * This is only the 'base' for the MST model. It gets composed with the
 * AttributeOwner partial type below.
 */
const ObjectType = types
  .model('ObjectTypeBase', {
    abstract: types.boolean,
    id: types.identifier,
    name: types.string,
    description: types.optional(types.string, ''),
    constraints: types.optional(types.string, ''),
    fsms: types.optional(types.array(FSM), []),
    multiplePropagationConstraints: types.optional(types.array(MultiplePropagationConstraint), []),
  })
  .views((self0) => {
    // Rebind `self` to the hand-declared IObjectType so the sibling views below
    // see each other (MST's `self` excludes the views defined in this block).
    // `self` and `self0` are the identical node at runtime — this is a type alias.
    const self = self0 as unknown as IObjectType;
    return {
    // Used in the logger and the report view
    get displayString() {
      return `Object ${self.name}`;
    },
    // Human-readable label, e.g. for `${object}` interpolation in messages.
    // (MST 7 forbids declaring a view named `toString`, so this is a plain view.)
    asString() {
      return self.name;
    },
    // convenience method to get the metamodel
    get metamodel(): IMetaModel {
      // up 2-levels because 1 level up is the array of object types.
      return getParent(self, 2);
    },
    // get the dependencies that have this object type as their dependent.
    get dependenciesAsDependent() {
      return self.metamodel.dependencies.filter((dep) => dep.dependent === self);
    },
    // get the dependencies that have this object type as their master.
    get dependenciesAsMaster() {
      return self.metamodel.dependencies.filter((dep) => dep.master === self);
    },
    // get all dependencies of this object type
    get dependencies() {
      return self.dependenciesAsMaster.concat(self.dependenciesAsDependent);
    },
    // get all masters of the dependencies in which this object type is a dependent
    get masters() {
      return self.dependenciesAsDependent.map((d) => d.master);
    },
    // get all inheritances in which this object type is the subtype
    get inheritancesAsSubtype() {
      return self.metamodel.inheritances.filter((i) => i.subtype === self);
    },
    // get all inheritances in which this object type is the supertype
    get inheritancesAsSupertype() {
      return self.metamodel.inheritances.filter((i) => i.supertype === self);
    },
    // get all the inheritances of this object type
    get inheritances() {
      return self.inheritancesAsSubtype.concat(self.inheritancesAsSupertype);
    },
    // get all supertypes of this object type
    get supertypes() {
      return self.inheritancesAsSubtype.map((i) => i.supertype);
    },
    // get all direct and indirect supertypes of this object type in a safe way: this one will work
    // even if there's an inheritance loop
    safeIndirectSupertypes(seen: IObjectType[] = []): IObjectType[] {
      if (seen.includes(self)) {
        // Break the loop
        return [];
      } else {
        const newSeen = seen.concat([self]);
        const indirectSupertypes = self.supertypes.flatMap((s) =>
          s.safeIndirectSupertypes(newSeen)
        );
        return self.supertypes.concat(indirectSupertypes);
      }
    },
    // get all direct and indirect subtypes of this object type in a safe way: this one will work
    // even if there's an inheritance loop
    safeIndirectSubtypes(seen: IObjectType[] = []): IObjectType[] {
      if (seen.includes(self)) {
        // Break the loop
        return [];
      } else {
        const newSeen = seen.concat([self]);
        const indirectSubtypes = self.subtypes.flatMap((s) => s.safeIndirectSubtypes(newSeen));
        return self.subtypes.concat(indirectSubtypes);
      }
    },
    // get all direct and indirect supertypes of this object type
    get indirectSupertypes() {
      return self.safeIndirectSupertypes();
    },
    // get all direct and indirect subtypes of this object type
    get indirectSubtypes() {
      return self.safeIndirectSubtypes();
    },
    // get all subtypes of this object type
    get subtypes() {
      return self.inheritancesAsSupertype.map((i) => i.subtype);
    },
    // get all methods that have this object type as their ownerObjectType
    get methods() {
      return self.metamodel.methods.filter((m) => m.ownerObjectType === self);
    },
    // get all acquired methods that have this object type as their
    // ownerObjectType
    get acquiredMethods() {
      return self.methods.filter((m) => m.provenance === 'ACQUIRED');
    },
    // get all owned methods that have this object type as their
    // ownerObjectType
    get ownedMethods() {
      return self.methods.filter((m) => m.provenance === 'OWNED');
    },
    // all inherited methods that have this object type as their
    // ownerObjectType
    get inheritedMethods() {
      return self.methods.filter((m) => m.provenance === 'INHERITED');
    },
    // all specialised methods that have this object type as their
    // ownerObjectType
    get specialisedMethods() {
      return self.methods.filter((m) => m.provenance === 'SPECIALISED');
    },
    // get all methods that have this object type as their ownerObjectType and
    // the given event as their ownerEventType
    methodsForEventType(eventId: string) {
      return self.methods.filter((m) => m.ownerEventType.id === eventId);
    },
    // get all inherited methods that have this object type as their ownerObjectType and
    // the given event as their ownerEventType
    inheritedMethodsForEventType(eventId: string) {
      return self.inheritedMethods.filter((m) => m.ownerEventType.id === eventId);
    },
    // get the top-down level
    get tdl() {
      const masterTDLs = self.dependenciesAsDependent.map((d) => d.master.tdl + 1);
      const supertypeTDLs = self.supertypes.map((s) => s.tdl + 1);
      return Math.max(0, ...masterTDLs, ...supertypeTDLs);
    },
    // get the bottom-up level
    get bul() {
      const dependentBULs = self.dependenciesAsMaster.map((d) => d.dependent.bul + 1);
      const subtypeBULs = self.subtypes.map((s) => s.bul + 1);
      return Math.max(0, ...dependentBULs, ...subtypeBULs);
    },
    // check if this object type is existence dependent of the given object type
    isExistenceDependentOf(other: IObjectType, start: IObjectType | null = null): boolean {
      if (start === self) {
        // We have a loop somewhere, can happen if we are checking an invalid model
        return false;
      }
      return (
        self.masters.some((o) => o === other || o.isExistenceDependentOf(other, start || self)) ||
        self.indirectSupertypes.some(
          (o) => o === other || o.isExistenceDependentOf(other, start || self)
        )
      );
    },
    // get the default FSM
    get defaultFSM() {
      return self.fsms.find((f) => f.type === 'DEFAULT');
    },
    // get the FSM selected for code generation
    get fsmForCodeGeneration() {
      return self.fsms.find((f) => f.codegeneration === true);
    },
    // Get an FSM by id
    getFSMById(id: string) {
      return self.fsms.find((f) => f.id === id);
    },
    // Get an MPC by id
    getMultiplePropagationConstraintById(id: string) {
      return self.multiplePropagationConstraints.find((mpc) => mpc.id === id);
    },
    // Get all path to another object type
    pathsTo(target: IObjectType) {
      return generatePathsBetween(self, target, []);
    },
    // Get the inheritance chain to another object type
    inheritancePathTo(supertype: IObjectType): IInheritance[] {
      if (supertype === self) {
        return [];
      } else if (self.inheritancesAsSubtype.length > 0) {
        const inheritance = self.inheritancesAsSubtype[0];
        return [inheritance].concat(inheritance.supertype.inheritancePathTo(supertype));
      } else {
        throw new Error(
          `In ${self.asString()}: ${supertype.asString()} not found along inheritance chain`
        );
      }
    },
    };
  })
  .actions((self) => ({
    setName(name: string) {
      self.name = name;
    },
    setDescription(description: string) {
      self.description = description;
    },
    setAbstract(abstract: boolean) {
      self.abstract = abstract;
    },
    setConstraints(constraints: string) {
      self.constraints = constraints;
    },
    addFSM(
      id: string,
      name: string,
      type: 'DEFAULT' | 'USER_DEFINED' | 'CALCULATED',
      codegeneration: boolean
    ) {
      const fsm = FSM.create({
        id,
        name,
        type,
        codegeneration,
      });
      self.fsms.push(fsm);
      return fsm;
    },
    // id? To be able to replay, we need to have serializable arguments!
    removeFSM(id: string) {
      const fsm = self.getFSMById(id);
      // MobX observable array has a 'remove' method!
      self.fsms.remove(fsm as Instance<typeof FSM>);
    },
    // id? To be able to replay, we need to have serializable arguments!
    addMultiplePropagationConstraint(
      id: string,
      type: 'EQUAL_TO' | 'NOT_EQUAL_TO',
      paths: SnapshotIn<typeof MultiplePropagationConstraint>['paths']
    ) {
      const mpc = MultiplePropagationConstraint.create({
        id,
        type,
        paths,
      });
      self.multiplePropagationConstraints.push(mpc);
      return mpc;
    },
    // id? To be able to replay, we need to have serializable arguments!
    removeMultiplePropagationConstraint(id: string) {
      const mpc = self.getMultiplePropagationConstraintById(id);
      // MobX observable array has a 'remove' method!
      self.multiplePropagationConstraints.remove(
        mpc as unknown as Instance<typeof MultiplePropagationConstraint>
      );
    },
  }));

// compose the base with Attribute Owner
const ObjectTypeModel = types.compose('ObjectType', ObjectType, AttributeOwner);

export default ObjectTypeModel;
